import React, { useEffect, useMemo, useState } from "react";

import {
  collection,
  getDocs,
  onSnapshot,
} from "firebase/firestore";

import {
  useSearchParams,
  useNavigate,
} from "react-router-dom";

import { db } from "../firebase";

import ProductCard from "../components/ProductCard";

import { useCart } from "../context/CartContext";

import "../styles/AllProducts.css";

export default function AllProducts() {

  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const [products, setProducts] = useState([]);

  const [shopProducts, setShopProducts] = useState([]);

  const [premiumShopIds, setPremiumShopIds] = useState(
    new Set()
  );

  const [loadingProducts, setLoadingProducts] =
    useState(true);

  const [loadingShopProducts, setLoadingShopProducts] =
    useState(true);

  const [search, setSearch] = useState("");

  const [category, setCategory] = useState(
    searchParams.get("category") || ""
  );

  const [brand, setBrand] = useState("");

  const {
    cart,
    addToCart,
    updateQty,
  } = useCart();


  /* =====================================================
     CATEGORY FROM URL + SCROLL TO TOP
  ===================================================== */

  useEffect(() => {

    const selectedCategory =
      searchParams.get("category") || "";

    // Update selected category from URL
    setCategory(selectedCategory);

    // Always start All Products from the top
    window.scrollTo(0, 0);

  }, [searchParams]);


  /* =====================================================
     LOAD NORMAL PRODUCTS
  ===================================================== */

  useEffect(() => {

    const loadProducts = async () => {

      try {

        setLoadingProducts(true);

        const snap = await getDocs(
          collection(db, "products")
        );

        const list = snap.docs
          .map((doc) => ({
            product_id: doc.id,
            source: "product",
            ...doc.data(),
          }))
          .sort((a, b) => {

            const ta = a.updatedAt?.toDate?.();

            const tb = b.updatedAt?.toDate?.();

            if (
              ta &&
              tb &&
              ta.getTime() !== tb.getTime()
            ) {
              return tb - ta;
            }

            const na = Number(
              String(a.product_id).replace(/\D/g, "")
            );

            const nb = Number(
              String(b.product_id).replace(/\D/g, "")
            );

            return nb - na;

          });

        setProducts(list);

      } catch (error) {

        console.error(
          "Failed to load products:",
          error
        );

      } finally {

        setLoadingProducts(false);

      }

    };

    loadProducts();

  }, []);


  /* =====================================================
     LOAD PREMIUM SHOPS
  ===================================================== */

  useEffect(() => {

    const unsubscribe = onSnapshot(
      collection(db, "shops"),

      (snapshot) => {

        const premiumIds = new Set();

        snapshot.docs.forEach((doc) => {

          const data = doc.data();

          const shopType =
            data["Type (Premium/Normal)"] ||
            data.Type ||
            "Normal";

          const isPremium =
            String(shopType)
              .toLowerCase()
              .includes("premium");

          if (isPremium) {

            premiumIds.add(doc.id);

          }

        });

        setPremiumShopIds(premiumIds);

      },

      (error) => {

        console.error(
          "Failed to load shop types:",
          error
        );

      }
    );

    return unsubscribe;

  }, []);


  /* =====================================================
     LOAD SHOP PRODUCTS
  ===================================================== */

  useEffect(() => {

    const unsubscribe = onSnapshot(
      collection(db, "shopproducts"),

      (snapshot) => {

        const list = snapshot.docs
          .map((doc) => ({

            id: doc.id,

            source: "shop",

            ...doc.data(),

          }))
          .filter(
            (p) => p.active !== false
          );

        setShopProducts(list);

        setLoadingShopProducts(false);

      },

      (error) => {

        console.error(
          "Failed to load shop products:",
          error
        );

        setLoadingShopProducts(false);

      }
    );

    return unsubscribe;

  }, []);


  /* =====================================================
     COMBINE + SHUFFLE BOTH COLLECTIONS
  ===================================================== */

  const allProducts = useMemo(() => {

    // Only allow products belonging to Premium shops

    const premiumShopProducts =
      shopProducts.filter(
        (product) =>
          product.shopId &&
          premiumShopIds.has(product.shopId)
      );

    const combined = [

      ...products,

      ...premiumShopProducts,

    ];

    // Shuffle products randomly

    for (
      let i = combined.length - 1;
      i > 0;
      i--
    ) {

      const j = Math.floor(
        Math.random() * (i + 1)
      );

      [combined[i], combined[j]] = [
        combined[j],
        combined[i],
      ];

    }

    return combined;

  }, [
    products,
    shopProducts,
    premiumShopIds,
  ]);


  /* =====================================================
     FILTER OPTIONS
  ===================================================== */

  const categories = useMemo(() => {

    return [
      ...new Set(
        allProducts
          .map((p) => p.category)
          .filter(Boolean)
      ),
    ].sort();

  }, [allProducts]);


  const brands = useMemo(() => {

    return [
      ...new Set(
        allProducts
          .map((p) => p.brand)
          .filter(Boolean)
      ),
    ].sort();

  }, [allProducts]);


  /* =====================================================
     FILTER PRODUCTS
  ===================================================== */

  const filteredProducts = useMemo(() => {

    const searchText =
      search.trim().toLowerCase();

    return allProducts.filter((item) => {

      const matchSearch =
        !searchText ||

        item.name
          ?.toLowerCase()
          .includes(searchText) ||

        item.brand
          ?.toLowerCase()
          .includes(searchText) ||

        item.category
          ?.toLowerCase()
          .includes(searchText) ||

        item.shopName
          ?.toLowerCase()
          .includes(searchText);

      const matchCategory =
        !category ||
        item.category === category;

      const matchBrand =
        !brand ||
        item.brand === brand;

      return (
        matchSearch &&
        matchCategory &&
        matchBrand
      );

    });

  }, [
    allProducts,
    search,
    category,
    brand,
  ]);


  /* =====================================================
     CART
  ===================================================== */

  const getCartItem = (productId) => {

    return cart.find(
      (item) =>
        item.type === "shop" &&
        (
          item.id === productId ||
          item.product_id === productId
        )
    );

  };


  /* =====================================================
     LOADING
  ===================================================== */

  const loading =
    loadingProducts ||
    loadingShopProducts;


  /* =====================================================
     RENDER SHOP PRODUCT
  ===================================================== */

  const renderShopProduct = (item) => {

    const cartItem = getCartItem(item.id);

    return (

      <div
        className="all-shop-product-card"
        key={`shop-${item.id}`}
      >

        {/* =================================================
           PRODUCT IMAGE
        ================================================= */}

        <img
          className="all-shop-product-image"
          src={
            item.image ||
            "https://via.placeholder.com/300"
          }
          alt={item.name}
        />


        <div className="all-shop-product-info">


          {/* =================================================
             PRODUCT NAME
          ================================================= */}

          <div className="all-shop-product-name">
            {item.name}
          </div>


          {/* =================================================
             SHOP NAME
          ================================================= */}

          {item.shopName && (

            <div className="all-shop-product-shop">
              🏪 {item.shopName}
            </div>

          )}


          {/* =================================================
             CATEGORY
          ================================================= */}

          {item.category && (

            <div className="all-shop-product-category">
              {item.category}
            </div>

          )}


          {/* =================================================
             STOCK
          ================================================= */}

          <div className="all-shop-product-stock">
            Stock: {item.stock ?? 0}
          </div>


          {/* =================================================
             PRICE
          ================================================= */}

          <div className="all-shop-product-price">
            ₹{item.price}
          </div>


          {/* =================================================
             ACTIONS
          ================================================= */}

          <div className="all-shop-product-action">


            {/* =================================================
               VIEW PRODUCT
            ================================================= */}

            <button
              className="all-shop-view-btn"
              onClick={() =>
                navigate(
                  `/product/${item.id}?source=shop`
                )
              }
            >
              View Product
            </button>


            {/* =================================================
               ADD TO CART
            ================================================= */}

            {!cartItem ? (

              <button
                className="all-shop-add-btn"
                disabled={
                  Number(item.stock) <= 0
                }
                onClick={() =>
                  addToCart({

                    type: "shop",

                    id: item.id,

                    shopId: item.shopId,

                    ownerUid: item.ownerUid,

                    shopName:
                      item.shopName || "",

                    name: item.name,

                    brand:
                      item.brand || "",

                    image:
                      item.image || "",

                    price:
                      Number(item.price),

                    mrp:
                      Number(
                        item.mrp ||
                        item.price
                      ),

                    stock:
                      Number(item.stock),

                  })
                }
              >

                {Number(item.stock) <= 0
                  ? "Out"
                  : "+ Add"}

              </button>

            ) : (


              /* =================================================
                 QUANTITY CONTROLS
              ================================================= */

              <div className="all-shop-qty-box">

                <button
                  className="all-shop-qty-btn"
                  onClick={() =>
                    updateQty(
                      cartItem.key,
                      cartItem.qty - 1
                    )
                  }
                >
                  −
                </button>


                <span className="all-shop-qty-text">
                  {cartItem.qty}
                </span>


                <button
                  className="all-shop-qty-btn"
                  disabled={
                    cartItem.qty >=
                    Number(item.stock)
                  }
                  onClick={() =>
                    updateQty(
                      cartItem.key,
                      cartItem.qty + 1
                    )
                  }
                >
                  +
                </button>

              </div>

            )}

          </div>

        </div>

      </div>

    );

  };


  /* =====================================================
     RENDER
  ===================================================== */

  return (

    <div className="all-products-page">


      {/* =================================================
         HEADER
      ================================================= */}

      <div className="all-products-header">

        <h2>
          All Products
        </h2>

        <div className="all-products-count">
          {filteredProducts.length} products
        </div>

      </div>


      {/* =================================================
         FILTERS
      ================================================= */}

      <div className="all-products-filters">


        {/* SEARCH */}

        <input
          className="all-products-search"
          type="text"
          placeholder="🔍 Search products, shops, brands..."
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
        />


        {/* CATEGORY */}

        <select
          className="all-products-dropdown"
          value={category}
          onChange={(e) =>
            setCategory(e.target.value)
          }
        >

          <option value="">
            All Categories
          </option>

          {categories.map((item) => (

            <option
              key={item}
              value={item}
            >
              {item}
            </option>

          ))}

        </select>


        {/* BRAND */}

        <select
          className="all-products-dropdown"
          value={brand}
          onChange={(e) =>
            setBrand(e.target.value)
          }
        >

          <option value="">
            All Brands
          </option>

          {brands.map((item) => (

            <option
              key={item}
              value={item}
            >
              {item}
            </option>

          ))}

        </select>

      </div>


      {/* =================================================
         CONTENT
      ================================================= */}

      {loading ? (

        <div className="all-products-loading">
          Loading products...
        </div>

      ) : filteredProducts.length === 0 ? (

        <div className="all-products-empty">
          No products found.
        </div>

      ) : (

        <div className="all-products-grid">

          {filteredProducts.map((item) => {


            /* =================================================
               NORMAL Eyal MART PRODUCTS
            ================================================= */

            if (item.source === "product") {

              return (

                <ProductCard
                  key={`product-${item.product_id}`}
                  product={item}
                />

              );

            }


            /* =================================================
               PREMIUM SHOP PRODUCTS
            ================================================= */

            return renderShopProduct(item);

          })}

        </div>

      )}

    </div>

  );

}